package fr.frux.feature.profile

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import fr.frux.app.resources.Res
import fr.frux.app.resources.profile_delete_code_confirm
import fr.frux.app.resources.profile_delete_code_confirm_submitting
import fr.frux.app.resources.profile_delete_code_invalid_format
import fr.frux.app.resources.profile_delete_code_invalid_or_expired
import fr.frux.app.resources.profile_delete_code_label
import fr.frux.app.resources.profile_delete_code_placeholder
import fr.frux.app.resources.profile_delete_code_resend
import fr.frux.app.resources.profile_delete_code_text
import fr.frux.app.resources.profile_delete_code_title
import fr.frux.app.resources.profile_delete_requesting_text
import fr.frux.app.resources.profile_delete_requesting_title
import fr.frux.app.resources.profile_delete_warning_confirm
import fr.frux.app.resources.profile_delete_warning_text
import fr.frux.app.resources.profile_delete_warning_title
import fr.frux.app.resources.profile_dialog_cancel
import fr.frux.app.resources.profile_export_ready
import fr.frux.app.resources.profile_export_unsupported
import fr.frux.app.resources.profile_logout_all_dialog_confirm
import fr.frux.app.resources.profile_logout_all_dialog_text
import fr.frux.app.resources.profile_logout_all_dialog_title
import fr.frux.app.resources.profile_logout_all_done
import fr.frux.core.ads.LocalAdsController
import fr.frux.core.ads.rewardedResultMessage
import fr.frux.core.designsystem.components.FruxTextField
import fr.frux.core.designsystem.components.LocalBottomNavPadding
import fr.frux.core.designsystem.components.SnackbarMessageEffect
import fr.frux.core.designsystem.error.userMessage
import fr.frux.core.designsystem.layout.FruxLayoutDefaults
import fr.frux.core.designsystem.layout.FruxLayoutMode
import fr.frux.core.designsystem.layout.LocalFruxLayoutMode
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.shared.identity.IdentityPolicy
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource

/**
 * Profil de l'utilisateur connecté (refonte visuelle #442, mock 2026-09-23).
 *
 * `Route.Profile.requiresAuth = true` : les guards de [fr.frux.navigation.NavigationState]
 * garantissent que cet écran n'est atteint qu'en session authentifiée. Les tuiles de compteurs
 * ne s'affichent que si [fr.frux.feature.profile.ProfileState.contributions] a pu être chargé :
 * pas de zéros inventés (#156).
 *
 * La déconnexion ne navigue pas elle-même : elle change l'état de session, et `App` réagit à la
 * transition sortante (pile vidée sur `Route.Auth`, surface carte purgée). Un 401 non récupérable
 * emprunte exactement le même chemin — une seule sortie de session, quelle qu'en soit l'origine (#48).
 * La suppression de compte réussie emprunte le même chemin (#194) : elle ne fait que déconnecter.
 *
 * Grand écran : seul écran large de la pleine zone (`wideFullZoneMetadata`, #443), il se pose
 * contre le rail jusqu'à `FruxLayoutDefaults.FullZoneWideMaxWidth`, sur le fond opaque de la
 * zone. À partir de 900 dp disponibles,
 * disposition deux colonnes de la maquette web (#442). En dessous, y compris en `Expanded`, la
 * disposition mobile dans la colonne de 640 dp des autres écrans de la zone : `BoxWithConstraints`
 * tranche sur la largeur réellement disponible plutôt que sur le mode de layout.
 *
 * Les composables visuels (bandeau, tuiles, cartes, dispositions mobile/grand écran) vivent dans
 * `ProfileComponents.kt`, ce fichier ne gardant que l'orchestration et les dialogues (#442).
 *
 * @param onOpenSettings Ouvre la sous-page Réglages.
 * @param onOpenMyTrees Ouvre la sous-page « Mes arbres » (arbres créés par l'utilisateur).
 * @param onOpenNotifications Ouvre l'écran Activité (#225 P0) — carte choisie ici plutôt qu'un
 *   5e onglet dans la barre du bas : les 4 onglets existants (Explorer/Récoltes/Favoris/Profil) +
 *   le FAB « Ajouter » n'ont pas de place pour un onglet de plus sans resserrer les cibles
 *   tactiles, alors qu'une carte supplémentaire (à côté de « Mes arbres »/« Réglages ») ne coûte
 *   rien à ce stade.
 * @param onOpenFeedback Ouvre le formulaire « Donner mon avis » (#437, ADR 0028).
 * @param onOpenPremium Ouvre l'écran Frux+ (#456, ADR 0016).
 * @param onOpenBlockedUsers Ouvre « Comptes masqués » (#515, ADR 0029).
 * @param onAddTree Ouvre l'écran d'ajout d'arbre depuis le rappel du premier arbre (compte sans arbre).
 */
@Composable
fun ProfileScreen(
    onOpenSettings: () -> Unit,
    onOpenMyTrees: () -> Unit,
    onOpenNotifications: () -> Unit,
    onOpenFeedback: () -> Unit,
    onOpenPremium: () -> Unit,
    onOpenBlockedUsers: () -> Unit,
    onAddTree: () -> Unit,
    viewModel: ProfileViewModel = rememberProfileViewModel(),
) {
    val state by viewModel.state.collectAsState()
    val user = state.user
    val snackbarHostState = remember { SnackbarHostState() }
    val uriHandler = LocalUriHandler.current

    // « Gérer mes choix publicitaires » (ADR 0030, #527) : le formulaire UMP est piloté par le
    // contrôleur, l'écran ne fait que l'ouvrir. Absent tant qu'UMP ne l'exige pas.
    val adsController = LocalAdsController.current
    val adsState by adsController.state.collectAsState()
    val scope = rememberCoroutineScope()
    val onOpenAdChoices: (() -> Unit)? =
        if (adsState.supported && adsState.privacyOptionsAvailable) {
            { scope.launch { adsController.openPrivacyOptions() } }
        } else {
            null
        }

    // Reprise de l'écran (#156, revue de fin de lot) : ce ViewModel survit à une navigation vers
    // « Mes arbres » (rememberViewModelStoreNavEntryDecorator, App.kt), donc l'init du VM ne
    // rejoue pas au retour. `Unit` se redéclenche à chaque entrée en composition de cet écran,
    // y compris ce retour de pile, ce qui rafraîchit les compteurs plutôt que de les figer.
    LaunchedEffect(Unit) {
        viewModel.refreshContributions()
    }

    // Message ponctuel (export réussi/échoué, renvoi de code échoué) : vit dans l'état du VM et
    // non dans un effet d'écran, l'entryProvider Nav3 étant recréé à chaque changement de pile (#145).
    SnackbarMessageEffect(
        trigger = state.transientMessage,
        message = state.transientMessage?.message(),
        snackbarHostState = snackbarHostState,
    ) { viewModel.onMessageShown() }

    // Vidéo récompensée (#531) : résultat porté par l'état du VM, comme les autres messages.
    SnackbarMessageEffect(
        trigger = state.rewardedResult,
        message = rewardedResultMessage(state.rewardedResult),
        snackbarHostState = snackbarHostState,
    ) { viewModel.onRewardedMessageShown() }

    // URL de don (#319) : idem, one-shot porté par l'état du VM plutôt qu'un effet nu, pour
    // survivre à la recréation de l'entryProvider Nav3.
    LaunchedEffect(state.pendingDonateUrl) {
        state.pendingDonateUrl?.let {
            uriHandler.openUri(it)
            viewModel.onDonateUrlOpened()
        }
    }

    // Pas d'insets système sur ce Scaffold (#593, #595) : la barre de navigation du bas les
    // consomme déjà (LocalBottomNavPadding, cf. RootChrome) et le bandeau peint sous la barre
    // d'état. Les laisser doublait le dégagement du bas et laissait une bande claire en haut.
    val bottomNavPadding = LocalBottomNavPadding.current
    Scaffold(
        snackbarHost = {
            SnackbarHost(snackbarHostState, modifier = Modifier.padding(bottom = bottomNavPadding))
        },
        containerColor = MaterialTheme.colorScheme.background,
        contentWindowInsets = WindowInsets(0, 0, 0, 0),
    ) { paddingValues ->
        if (user == null) {
            // Session en cours de transition (ex: juste après déconnexion, avant que
            // l'appelant ne retire cet écran du back stack) : ne rien afficher plutôt
            // que de crasher sur un email/username absent.
            Box(modifier = Modifier.fillMaxSize().padding(paddingValues))
            return@Scaffold
        }

        val layoutMode = LocalFruxLayoutMode.current
        if (layoutMode == FruxLayoutMode.Expanded) {
            // Jamais la disposition mobile à côté du rail (#596) : son bandeau bord à bord, posé
            // dans une colonne, s'arrêtait au rail et laissait la barre d'état mi-teinte. Trop
            // étroit pour deux colonnes, les mêmes blocs s'empilent.
            BoxWithConstraints(modifier = Modifier.fillMaxSize().padding(paddingValues)) {
                ProfileExpandedContent(
                    user = user,
                    state = state,
                    viewModel = viewModel,
                    onOpenSettings = onOpenSettings,
                    onOpenMyTrees = onOpenMyTrees,
                    onOpenNotifications = onOpenNotifications,
                    onOpenFeedback = onOpenFeedback,
                    onOpenPremium = onOpenPremium,
                    onOpenBlockedUsers = onOpenBlockedUsers,
                    onAddTree = onAddTree,
                    onOpenAdChoices = onOpenAdChoices,
                    twoColumns = maxWidth >= TwoColumnMinWidth,
                )
            }
        } else {
            ProfileMobileContent(
                user = user,
                state = state,
                viewModel = viewModel,
                onOpenSettings = onOpenSettings,
                onOpenMyTrees = onOpenMyTrees,
                onOpenNotifications = onOpenNotifications,
                onOpenFeedback = onOpenFeedback,
                onOpenPremium = onOpenPremium,
                onOpenBlockedUsers = onOpenBlockedUsers,
                onAddTree = onAddTree,
                onOpenAdChoices = onOpenAdChoices,
                modifier = Modifier.padding(paddingValues),
            )
        }
    }

    AccountDeletionDialogs(state.deletion, viewModel)

    if (state.logoutAllConfirmationVisible) {
        LogoutAllConfirmationDialog(viewModel)
    }
}

/** Seuil grand écran interne à cet écran (mock Web, #442) — cf. docstring de [ProfileScreen]. */
internal val TwoColumnMinWidth = 900.dp

/**
 * Confirmation sobre avant « Déconnecter tous les appareils » (#26, C8) : même gabarit que
 * [AccountDeletionWarningDialog], sans teinte error — ce geste coupe des sessions, pas le compte.
 */
@Composable
private fun LogoutAllConfirmationDialog(viewModel: ProfileViewModel) {
    AlertDialog(
        onDismissRequest = viewModel::cancelLogoutAllConfirmation,
        title = { Text(stringResource(Res.string.profile_logout_all_dialog_title)) },
        text = {
            Text(stringResource(Res.string.profile_logout_all_dialog_text))
        },
        confirmButton = {
            TextButton(onClick = viewModel::confirmLogoutAllDevices) {
                Text(stringResource(Res.string.profile_logout_all_dialog_confirm))
            }
        },
        dismissButton = {
            TextButton(onClick = viewModel::cancelLogoutAllConfirmation) {
                Text(stringResource(Res.string.profile_dialog_cancel))
            }
        },
    )
}

/**
 * Dialogues du flux de suppression (#194) : un composable dédié plutôt que des `if` empilés dans
 * [ProfileScreen], pour garder la structure à trois étapes lisible d'un coup d'œil.
 */
@Composable
private fun AccountDeletionWarningDialog(viewModel: ProfileViewModel) {
    AlertDialog(
        onDismissRequest = viewModel::cancelAccountDeletion,
        title = { Text(stringResource(Res.string.profile_delete_warning_title)) },
        text = {
            Text(stringResource(Res.string.profile_delete_warning_text))
        },
        confirmButton = {
            TextButton(
                onClick = viewModel::requestDeletionCode,
                colors = ButtonDefaults.textButtonColors(contentColor = MaterialTheme.colorScheme.error),
            ) {
                Text(stringResource(Res.string.profile_delete_warning_confirm))
            }
        },
        dismissButton = {
            TextButton(onClick = viewModel::cancelAccountDeletion) {
                Text(stringResource(Res.string.profile_dialog_cancel))
            }
        },
    )
}

@Composable
private fun AccountDeletionRequestingCodeDialog() {
    AlertDialog(
        onDismissRequest = {},
        title = { Text(stringResource(Res.string.profile_delete_requesting_title)) },
        text = {
            Row(verticalAlignment = Alignment.CenterVertically) {
                CircularProgressIndicator(modifier = Modifier.size(24.dp))
                Spacer(modifier = Modifier.width(FruxSpacing.md))
                Text(stringResource(Res.string.profile_delete_requesting_text))
            }
        },
        confirmButton = {},
    )
}

@Composable
private fun AccountDeletionEnterCodeDialog(
    deletion: AccountDeletionState.EnterCode,
    viewModel: ProfileViewModel,
) {
    AlertDialog(
        onDismissRequest = viewModel::cancelAccountDeletion,
        title = { Text(stringResource(Res.string.profile_delete_code_title)) },
        text = {
            Column {
                Text(stringResource(Res.string.profile_delete_code_text))
                Spacer(modifier = Modifier.height(FruxSpacing.md))
                FruxTextField(
                    value = deletion.code,
                    onValueChange = viewModel::onDeletionCodeChange,
                    label = stringResource(Res.string.profile_delete_code_label),
                    placeholder = stringResource(Res.string.profile_delete_code_placeholder),
                    isError = deletion.error != null,
                    supportingText = deletion.error?.message(),
                    enabled = !deletion.isSubmitting,
                    keyboardOptions =
                        KeyboardOptions(
                            keyboardType = KeyboardType.NumberPassword,
                            imeAction = ImeAction.Done,
                        ),
                    keyboardActions = KeyboardActions(onDone = { viewModel.confirmAccountDeletion() }),
                )
                Spacer(modifier = Modifier.height(FruxSpacing.sm))
                TextButton(
                    onClick = viewModel::requestDeletionCode,
                    enabled = !deletion.isSubmitting,
                ) {
                    Text(stringResource(Res.string.profile_delete_code_resend))
                }
            }
        },
        confirmButton = {
            TextButton(
                onClick = viewModel::confirmAccountDeletion,
                enabled = !deletion.isSubmitting,
                colors = ButtonDefaults.textButtonColors(contentColor = MaterialTheme.colorScheme.error),
            ) {
                Text(
                    if (deletion.isSubmitting) {
                        stringResource(Res.string.profile_delete_code_confirm_submitting)
                    } else {
                        stringResource(Res.string.profile_delete_code_confirm)
                    },
                )
            }
        },
        dismissButton = {
            TextButton(onClick = viewModel::cancelAccountDeletion, enabled = !deletion.isSubmitting) {
                Text(stringResource(Res.string.profile_dialog_cancel))
            }
        },
    )
}

@Composable
private fun AccountDeletionDialogs(
    deletion: AccountDeletionState,
    viewModel: ProfileViewModel,
) {
    when (deletion) {
        AccountDeletionState.Idle -> {
            Unit
        }

        AccountDeletionState.Warning -> {
            AccountDeletionWarningDialog(viewModel)
        }

        AccountDeletionState.RequestingCode -> {
            AccountDeletionRequestingCodeDialog()
        }

        is AccountDeletionState.EnterCode -> {
            AccountDeletionEnterCodeDialog(deletion, viewModel)
        }
    }
}

/** Libellé du message ponctuel du profil (#176). */
@Composable
private fun ProfileMessage.message(): String =
    when (this) {
        ProfileMessage.AllDevicesLoggedOut -> stringResource(Res.string.profile_logout_all_done)
        ProfileMessage.ExportReady -> stringResource(Res.string.profile_export_ready)
        ProfileMessage.ExportNotSupported -> stringResource(Res.string.profile_export_unsupported)
        is ProfileMessage.Failure -> error.userMessage()
    }

/** Libellé du refus de confirmation de suppression (#194). */
@Composable
private fun AccountDeletionError.message(): String =
    when (this) {
        AccountDeletionError.CodeInvalidFormat -> {
            stringResource(Res.string.profile_delete_code_invalid_format, IdentityPolicy.OTP_CODE_LENGTH)
        }

        AccountDeletionError.CodeInvalidOrExpired -> {
            stringResource(Res.string.profile_delete_code_invalid_or_expired)
        }

        is AccountDeletionError.Failure -> {
            error.userMessage()
        }
    }
